import { useEffect, useState, useRef } from 'react';
import { ColorResult, PhotoshopPicker } from 'react-color';
import { Box, BoxProps, Button, Flex, FormErrorMessageProps } from '@chakra-ui/react';
import { FormErrorMessage } from 'components/UI/forms';
import { Input, InputProps } from 'components/UI/Input';

export type ColourPickerProps = Omit<InputProps, 'onChange'> & {
  name: string;
  error?: string;
  value: string;
  errorProps?: FormErrorMessageProps;
  containerProps?: BoxProps;
  inputProps?: InputProps;
  onChange: (name: string, colour?: string) => void;
};

const ColourPicker = ({
  error,
  errorProps,
  inputProps,
  containerProps,
  name,
  size,
  value,
  isDisabled,
  onChange,
}: ColourPickerProps) => {
  const [showPicker, setShowPicker] = useState(false);
  const [colourSelected, setColourSelected] = useState<string>('#FFF');

  const ref = useRef<HTMLDivElement>(null);
  const colourPickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setColourSelected(value ?? '#FFF');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      const target = event.target as Node;
      if (
        ref.current &&
        colourPickerRef.current &&
        !ref.current.contains(target) &&
        !colourPickerRef.current.contains(target)
      ) {
        setShowPicker(false);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [ref, colourPickerRef]);

  const handleColorPick = (valueColour: ColorResult) => {
    setColourSelected(valueColour?.hex);
  };

  const handleTogglePicker = () => {
    if (inputProps?.isDisabled) return;
    setShowPicker((prevPicker) => !prevPicker);
  };

  const renderPicker = showPicker ? (
    <Box position="absolute" zIndex={2} ref={colourPickerRef}>
      <PhotoshopPicker
        color={colourSelected ?? '#fff'}
        data-testid="color-picker"
        onChangeComplete={handleColorPick}
        onAccept={() => {
          handleTogglePicker();

          if (!onChange) return;

          onChange(name, colourSelected);
        }}
        onCancel={() => {
          handleTogglePicker();
          setColourSelected(value);
        }}
      />
    </Box>
  ) : null;

  const getDisplayWidth = () => {
    switch (size) {
      case 'lg':
        return 12;
      case 'sm':
        return 8;
      default:
        return 10;
    }
  };

  return (
    <Box {...containerProps}>
      <Flex ref={ref}>
        <Input
          type="text"
          name={name}
          id={name}
          value={value}
          isReadOnly
          onClick={handleTogglePicker}
          isDisabled={isDisabled}
          size={size}
          {...inputProps}
        />
        <Button
          variant="unstyled"
          sx={{
            bgColor: value ?? 'gray.100',
            w: getDisplayWidth(),
            ml: 1.5,
            h: 'auto',
            opacity: isDisabled ? 0.4 : 1,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            _focus: {
              boxShadow: 'none',
            },
            minW: 0,
            borderRadius: 0,
          }}
          onClick={() => handleTogglePicker()}
        />
      </Flex>
      {renderPicker}
      <FormErrorMessage {...errorProps}>{error}</FormErrorMessage>
    </Box>
  );
};

export { ColourPicker };
